import {
  ActionIcon,
  Button,
  Group,
  Modal,
  MultiSelect,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconPencil,
  IconMail,
  IconPaperclip,
  IconSend,
  IconX,
} from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import { sendAssistantEmailDraft } from "api";
import ActionAttachmentBadges from "components/Actions/ActionAttachmentBadges/ActionAttachmentBadges";
import { useUser } from "stores/useUser";
import { useAssistantWidgetStatus } from "stores/useAssistantWidgetStatus";
import {
  deleteEmailDraftAttachments,
  getEmailDraftAttachmentFileKey,
  uploadEmailDraftAttachments,
  validateEmailDraftAttachmentFiles,
} from "utils/emailDraftAttachments";
import { getTeamOptions, notify } from "utils/common";
import css from "./MicChatDrawer.module.scss";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const getRecipientEmails = (recipients) =>
  Array.isArray(recipients)
    ? recipients
        .map((recipient) => normalizeEmail(recipient.email))
        .filter((email) => EMAIL_PATTERN.test(email))
    : [];

const createEmailOptions = (emails) =>
  Array.from(new Set(emails))
    .filter((email) => EMAIL_PATTERN.test(email))
    .map((email) => ({ value: email, label: email }));

const createRecipientOptions = (recipients) =>
  Array.isArray(recipients)
    ? recipients
        .map((recipient) => {
          const email = normalizeEmail(recipient.email);
          return {
            value: email,
            label: recipient.displayName || recipient.email || email,
          };
        })
        .filter((recipient) => EMAIL_PATTERN.test(recipient.value))
    : [];

const mergeEmailOptions = (...optionLists) => {
  const seen = new Set();
  return optionLists.flat().flatMap((option) => {
    const value = normalizeEmail(option?.value);
    if (!EMAIL_PATTERN.test(value) || seen.has(value)) return [];

    seen.add(value);
    return {
      value,
      label: option.label || value,
    };
  });
};

const createUniqueEmails = (emails) =>
  Array.from(new Set((emails || []).map(normalizeEmail))).filter((email) =>
    EMAIL_PATTERN.test(email),
  );

const EmailDraftWidget = ({
  draft,
  statusKey,
  portalZIndex,
}) => {
  const { user } = useUser();
  const persistedStatus = useAssistantWidgetStatus(
    (state) => state.statusByKey[statusKey],
  );
  const markStatus = useAssistantWidgetStatus((state) => state.markStatus);
  const fileInputRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [toEmails, setToEmails] = useState(
    getRecipientEmails(draft.toRecipients),
  );
  const [ccEmails, setCcEmails] = useState(
    getRecipientEmails(draft.ccRecipients),
  );
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(persistedStatus === "sent");
  const [isCancelled, setIsCancelled] = useState(persistedStatus === "cancelled");
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);

  const options = useMemo(
    () =>
      mergeEmailOptions(
        getTeamOptions(),
        createRecipientOptions(draft.toRecipients),
        createRecipientOptions(draft.ccRecipients),
        createEmailOptions([...toEmails, ...ccEmails]),
      ),
    [ccEmails, draft.ccRecipients, draft.toRecipients, toEmails],
  );
  const ccOptions = useMemo(() => {
    const toEmailSet = new Set(toEmails.map(normalizeEmail));
    return options.filter(
      (option) => !toEmailSet.has(normalizeEmail(option.value)),
    );
  }, [options, toEmails]);
  const canSend = Boolean(
    !isSending &&
      !isSent &&
      !isCancelled &&
      (toEmails.length > 0 || ccEmails.length > 0) &&
      subject.trim() &&
      body.trim(),
  );
  const statusCopy = isSent
    ? "Sent"
    : isCancelled
      ? "Cancelled"
      : "Ready to review";
  const attachmentsDisabled = isSending || isSent || isCancelled;

  const getQueuedAttachmentId = (file, index) =>
    `${getEmailDraftAttachmentFileKey(file)}-${index}`;

  const queuedAttachments = attachmentFiles.map((file, index) => ({
    id: getQueuedAttachmentId(file, index),
    name: file.name,
    size: file.size,
    contentType: file.type,
    uploading: isSending,
    progress: uploadProgress[getEmailDraftAttachmentFileKey(file)] || 0,
  }));

  const clearAttachments = () => {
    setAttachmentFiles([]);
    setUploadProgress({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCreateEmail = (query) => {
    const value = normalizeEmail(query);
    if (!EMAIL_PATTERN.test(value)) return null;
    return { value, label: value };
  };

  const handleToEmailsChange = (emails) => {
    const nextToEmails = createUniqueEmails(emails);
    setToEmails(nextToEmails);
    setCcEmails((currentCcEmails) =>
      currentCcEmails.filter((email) => !nextToEmails.includes(email)),
    );
  };

  const handleCcEmailsChange = (emails) => {
    setCcEmails(
      createUniqueEmails(emails).filter((email) => !toEmails.includes(email)),
    );
  };

  const handleAddAttachmentFiles = (fileList) => {
    if (attachmentsDisabled) return;

    const files = Array.from(fileList || []);
    const { accepted, rejected } = validateEmailDraftAttachmentFiles(
      files,
      attachmentFiles,
    );

    if (accepted.length) {
      setAttachmentFiles((currentFiles) => [...currentFiles, ...accepted]);
    }

    rejected.forEach((message) => notify(message, "error"));
  };

  const handleSend = async () => {
    if (!canSend) return;

    setIsSending(true);
    let uploadedAttachments = [];
    try {
      const initialProgress = attachmentFiles.reduce((progress, file) => {
        progress[getEmailDraftAttachmentFileKey(file)] = 0;
        return progress;
      }, {});

      setUploadProgress(initialProgress);
      uploadedAttachments = await uploadEmailDraftAttachments({
        files: attachmentFiles,
        user,
        onProgress: (file, progress) => {
          setUploadProgress((currentProgress) => ({
            ...currentProgress,
            [getEmailDraftAttachmentFileKey(file)]: progress,
          }));
        },
      });

      const response = await sendAssistantEmailDraft({
        toEmails,
        ccEmails,
        subject: subject.trim(),
        body: body.trim(),
        attachments: uploadedAttachments,
      });

      if (!response?.status) {
        throw new Error(response?.message || "Failed to send email");
      }

      setIsSent(true);
      markStatus(statusKey, "sent");
      setIsExpanded(false);
      clearAttachments();
      notify("Email sent successfully", "success");
    } catch (error) {
      if (uploadedAttachments.length) {
        await deleteEmailDraftAttachments(uploadedAttachments);
      }
      notify(error.message || "Failed to send email", "error");
    } finally {
      setIsSending(false);
      setUploadProgress({});
    }
  };

  const handleCancel = () => {
    if (isSending || isSent) return;
    setIsCancelled(true);
    markStatus(statusKey, "cancelled");
    setIsExpanded(false);
    clearAttachments();
  };

  const handleRemoveAttachment = (attachment) => {
    setAttachmentFiles((files) =>
      files.filter(
        (file, index) => getQueuedAttachmentId(file, index) !== attachment.id,
      ),
    );
  };

  // Shared between the expanded editor and the collapsed summary (where it
  // appears only while the draft has no recipients, e.g. an ambient
  // email_followup whose recipient the agent could not verify).
  const toRecipientsField = (
    <MultiSelect
      zIndex={portalZIndex}
      label="To"
      data={options}
      value={toEmails}
      onChange={handleToEmailsChange}
      searchable
      creatable
      disabled={isSent || isCancelled}
      getCreateLabel={(query) =>
        EMAIL_PATTERN.test(normalizeEmail(query))
          ? `Add ${normalizeEmail(query)}`
          : "Enter an email"
      }
      onCreate={handleCreateEmail}
      placeholder="Add recipients"
      classNames={{
        root: css.calendarDraftInputRoot,
        input: css.calendarDraftInput,
        label: css.calendarDraftLabel,
      }}
    />
  );

  const editor = (
    <div className={css.emailDraftEditor}>
      {toRecipientsField}

      <MultiSelect
        zIndex={portalZIndex}
        label="Cc"
        data={ccOptions}
        value={ccEmails}
        onChange={handleCcEmailsChange}
        searchable
        creatable
        disabled={isSent || isCancelled}
        getCreateLabel={(query) =>
          EMAIL_PATTERN.test(normalizeEmail(query))
            ? `Add ${normalizeEmail(query)}`
            : "Enter an email"
        }
        onCreate={handleCreateEmail}
        placeholder="Add Cc"
        classNames={{
          root: css.calendarDraftInputRoot,
          input: css.calendarDraftInput,
          label: css.calendarDraftLabel,
        }}
      />

      <TextInput
        label="Subject"
        value={subject}
        onChange={(event) => setSubject(event.currentTarget.value)}
        disabled={isSent || isCancelled}
        classNames={{
          root: css.calendarDraftInputRoot,
          input: css.calendarDraftInput,
          label: css.calendarDraftLabel,
        }}
      />

      <Textarea
        label="Message"
        value={body}
        onChange={(event) => setBody(event.currentTarget.value)}
        disabled={isSent || isCancelled}
        minRows={8}
        autosize
        classNames={{
          root: css.calendarDraftInputRoot,
          input: css.emailDraftBodyInput,
          label: css.calendarDraftLabel,
        }}
      />

      <div
        className={`${css.emailDraftAttachmentDropzone} ${
          isDraggingAttachments ? css.emailDraftAttachmentDropzoneActive : ""
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!attachmentsDisabled) setIsDraggingAttachments(true);
        }}
        onDragLeave={() => setIsDraggingAttachments(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDraggingAttachments(false);
          handleAddAttachmentFiles(event.dataTransfer.files);
        }}
      >
        <Group position="apart" spacing="xs" noWrap>
          <div>
            <Text className={css.calendarDraftLabel}>Attachments</Text>
            <Text size="xs" color="#667085">
              {attachmentFiles.length
                ? `${attachmentFiles.length} file${
                    attachmentFiles.length === 1 ? "" : "s"
                  } attached`
                : "No files attached"}
            </Text>
          </div>
          <Button
            compact
            variant="default"
            leftIcon={<IconPaperclip size={14} />}
            disabled={attachmentsDisabled}
            onClick={() => fileInputRef.current?.click()}
          >
            Attach
          </Button>
        </Group>
        <ActionAttachmentBadges
          attachments={queuedAttachments}
          removable={!attachmentsDisabled}
          onRemove={handleRemoveAttachment}
        />
      </div>
    </div>
  );

  return (
    <div className={css.emailDraftCard}>
      <input
        ref={fileInputRef}
        className={css.emailDraftFileInput}
        type="file"
        multiple
        onChange={(event) => {
          handleAddAttachmentFiles(event.currentTarget.files);
          event.currentTarget.value = "";
        }}
      />
      <Group position="apart" align="flex-start" spacing="sm" noWrap>
        <div className={css.calendarDraftTitleGroup}>
          <span
            className={`${css.calendarDraftStatusIcon} ${
              isSent ? css.calendarDraftStatusReady : css.emailDraftStatusIcon
            }`}
          >
            {isSent ? <IconCheck size={14} /> : <IconMail size={14} />}
          </span>
          <div>
            <Text weight={800} size="sm" color="#253047">
              Email draft
            </Text>
            <Text size="xs" color="#667085">
              {statusCopy}
            </Text>
          </div>
        </div>
        <Group spacing={4} noWrap>
          {isSent && <span className={css.calendarDraftPill}>Sent</span>}
          {attachmentFiles.length > 0 && (
            <span className={css.emailDraftAttachmentPill}>
              <IconPaperclip size={12} />
              {attachmentFiles.length}
            </span>
          )}
          <ActionIcon
            variant="subtle"
            color="dark"
            title="Edit email draft"
            onClick={() => setIsExpanded(true)}
          >
            <IconPencil size={15} />
          </ActionIcon>
        </Group>
      </Group>

      <div className={css.emailDraftSummary}>
        {toEmails.length > 0 || isSent || isCancelled ? (
          <div>
            <Text className={css.calendarDraftLabel}>To</Text>
            <Text size="sm" color="#253047" className={css.emailDraftValue}>
              {toEmails.length ? toEmails.join(", ") : "No primary recipients"}
            </Text>
          </div>
        ) : (
          <div>
            {toRecipientsField}
            <div className={css.calendarDraftNotice}>
              <div className={css.calendarDraftWarningRow}>
                <IconAlertTriangle size={13} />
                <span>Add at least one recipient to send this email</span>
              </div>
            </div>
          </div>
        )}
        {ccEmails.length > 0 && (
          <div>
            <Text className={css.calendarDraftLabel}>Cc</Text>
            <Text size="sm" color="#253047" className={css.emailDraftValue}>
              {ccEmails.join(", ")}
            </Text>
          </div>
        )}
        <div>
          <Text className={css.calendarDraftLabel}>Subject</Text>
          <Text size="sm" color="#253047" weight={650}>
            {subject || "No subject"}
          </Text>
        </div>
        <Text size="sm" color="#475467" className={css.emailDraftPreview}>
          {body || "No message"}
        </Text>
        {attachmentFiles.length > 0 && (
          <div>
            <Text className={css.calendarDraftLabel}>Attachments</Text>
            <ActionAttachmentBadges
              attachments={queuedAttachments}
              removable={!attachmentsDisabled}
              onRemove={handleRemoveAttachment}
            />
          </div>
        )}
      </div>

      <Group position="apart" spacing="xs" className={css.calendarDraftFooter}>
        <Button
          compact
          variant="subtle"
          leftIcon={<IconPaperclip size={14} />}
          disabled={attachmentsDisabled}
          onClick={() => fileInputRef.current?.click()}
          className={css.emailDraftQuickAttach}
        >
          Attach
        </Button>
        <Group spacing="xs" noWrap>
          <Button
            compact
            variant="default"
            disabled={isSending || isSent || isCancelled}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            compact
            leftIcon={<IconSend size={14} />}
            disabled={!canSend}
            loading={isSending}
            onClick={handleSend}
            className={css.calendarDraftConfirm}
          >
            Send
          </Button>
        </Group>
      </Group>

      <Modal
        zIndex={portalZIndex}
        opened={isExpanded}
        onClose={() => setIsExpanded(false)}
        title="Email draft"
        centered
        size="lg"
        transition="pop"
        transitionDuration={180}
      >
        {editor}
        <Group position="right" spacing="xs" className={css.emailDraftModalFooter}>
          <Button
            compact
            variant="default"
            leftIcon={<IconX size={14} />}
            disabled={isSending}
            onClick={() => setIsExpanded(false)}
          >
            Cancel
          </Button>
          <Button
            compact
            leftIcon={<IconSend size={14} />}
            disabled={!canSend}
            loading={isSending}
            onClick={handleSend}
            className={css.calendarDraftConfirm}
          >
            Send
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default EmailDraftWidget;
